import { Column, EmptyDiv, FormContext, InfoDiv } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import { useGetUser } from '@/hooks/useGetUser';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetMinimumPricingConfigurationQuery,
  useGetPricingConfigurationQuery,
  useUpsertOrganizationPricingConfigurationMutation,
} from '@/store/slices/pricingConfigurationSlice';
import { Text, Button, Icon, IconType } from '@/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';
import { valuesToFormRows } from '@/pages/pricing-forms/pricingFormMapping';
import { usePricingChannel } from '@/pages/pricing-forms/usePricingChannel';
import { channelValues, toRates, versionOf } from '@/services/Pricing/pricing-configuration.service';

function OrganizationPricing() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { currentUser } = useGetUser();
  const [isReadOnly, setIsReadOnly] = useState(true);
  const companyArg = currentUser.companyId ? { organizationId: currentUser.companyId } : skipToken;

  const {
    data: defaults,
    isLoading: isDefaultsLoading,
    isError: isDefaultsError,
  } = useGetDefaultPricingConfigurationQuery(companyArg);
  const { data: minimums, isLoading: isMinimumsLoading } =
    useGetMinimumPricingConfigurationQuery(companyArg);
  const { data: existing, isLoading: isExistingLoading } = useGetPricingConfigurationQuery(
    id ? { organizationId: id } : skipToken,
  );

  const [upsertOrgPricingConfig, { isLoading: isSaving }] =
    useUpsertOrganizationPricingConfigurationMutation();

  const { channel, setChannel } = usePricingChannel();
  const channelDefaults = useMemo(() => channelValues(defaults, channel), [defaults, channel]);
  const channelMinimums = useMemo(() => channelValues(minimums, channel), [minimums, channel]);
  const channelExisting = useMemo(() => channelValues(existing, channel), [existing, channel]);
  const existingRates = useMemo(() => toRates(existing), [existing]);

  const { activeCountry, setActiveCountry, submitHandler, savedAt } = usePricingForm({
    baseline: channelDefaults,
    existing: channelExisting,
    existingRates,
    channel,
    submit: (nextRates) =>
      upsertOrgPricingConfig({
        organizationId: id ?? '',
        payload: { rates: nextRates, expectedUpdatedAt: versionOf(existing) },
      }),
    messages: {
      success: t('Messages.organization_pricing_update_success'),
      error: t('Messages.organization_pricing_update_error'),
      conflict: t('Messages.pricing_update_conflict'),
    },
  });

  const defaultFormValues = useMemo(
    () => ({
      channel,
      rows: valuesToFormRows(
        Object.keys(channelExisting).length ? channelExisting : channelDefaults,
      ),
    }),
    [channel, channelExisting, channelDefaults],
  );

  if (isDefaultsLoading || isMinimumsLoading || isExistingLoading) {
    return <SkeletonList />;
  }

  if (isDefaultsError || !defaults) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={defaultFormValues}>
      <Column gapSize='24'>
        {isReadOnly && (
          <InfoDiv>
            <Text size='s' weight='light' color='base-grey'>
              {t('Pricing.organization_pricing_comment')}
            </Text>
            <Button
              type='button'
              variant='outline'
              onClick={() => setIsReadOnly(false)}
              testId='customize-pricing'
            >
              <Icon type={IconType.Pencil_Edit} width={24} height={24} />
              <Text size='s' weight='medium' color='base-grey'>
                {t('Pricing.customize')}
              </Text>
            </Button>
          </InfoDiv>
        )}
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='org'
            defaults={channelDefaults}
            minimums={channelMinimums}
            existingValues={channelExisting}
            isReadOnly={isReadOnly}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
            channel={channel}
            onChannelChange={setChannel}
            savedAt={savedAt}
          />
        </Column>
        <PricingDiffFooter
          baseline={channelDefaults}
          existing={channelExisting}
          isSaving={isSaving}
          isReadOnly={isReadOnly}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default OrganizationPricing;
